import { MinusIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import { Button, Input, Modal } from 'react-daisyui';
import { Trans, useTranslation } from 'react-i18next';
import { useSubscriptionManagerContext } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.context';
import { useSettingsContext } from 'src/pages/Settings/Settings.context';
import { SettingsTabs } from 'src/pages/Settings/Settings.enum';
import { useACLContext } from 'src/providers/ACLProvider/ACL.hook';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import { resolveSeatQuote } from 'src/providers/SubscriptionProvider/useSeatPurchase.functions';
import { CENTS_TO_DOLLARS_CONVERSION_FACTOR } from '../../../SubscriptionManager.constants';
import CardSelector from '../../CardSelector/CardSelector.component';

export default function ManageUsersModal() {
  const {
    showManageUsersModal,
    toggleManageUsersModal,
    selectedPaymentMethod,
    toggleAddPaymentMethodModal,
    updateSeatsPlan,
    updatingPlan,
    products,
  } = useSubscriptionManagerContext();
  const { isLegacyPlan } = useACLContext();
  const { subscription } = useSubscriptionContext();
  const { currentSettingsInfo, setSelectedTab } = useSettingsContext();
  const { t } = useTranslation('settings/SubscriptionManager');

  const [userError, setUserError] = useState(false);
  const [extraSeats, setExtraSeats] = useState<number>(0);

  const longDateConfig: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  const conversionFormattedDate = subscription.endDate.toLocaleString(
    'es-MX',
    longDateConfig,
  );

  // U1 — the ONE seat-price resolution, shared with the Team tab's add-teammate flow.
  // This used to be an inline chain ending in `?? 0`, which meant the Subscription tab
  // could render a $0 seat for exactly the states where the Team tab now refuses to
  // quote. Resolving both surfaces through the same function is what makes them
  // structurally unable to disagree.
  const seatQuote = resolveSeatQuote({
    status: subscription.status,
    planCode: subscription.planCode,
    currency: subscription.currency,
    purchasedSeatsCost: subscription.purchasedSeatsCost,
    allRelatedProducts: products?.allRelatedProducts,
    availableForPurchase: products?.availableForPurchase,
  });

  const costInCents = isLegacyPlan
    ? subscription.purchasedSeatsCost
    : seatQuote.available ? seatQuote.recurringAmountCents : 0;

  // Absence of a price, not a zero price. The control below stays blocked either way;
  // what changes is that a real $0 can no longer be mistaken for "we don't know".
  const isMissingPrice = !isLegacyPlan && !seatQuote.available;

  const total = (Math.abs(extraSeats) * costInCents) / CENTS_TO_DOLLARS_CONVERSION_FACTOR;

  return (
    <Modal
      open={showManageUsersModal}
      backdrop
      onClose={() => toggleManageUsersModal(false)}
      className="rounded-lg p-0 min-w-[39.375rem]"
      data-testid="subscription-modal"
    >
      <Modal.Header className="flex justify-between items-center mb-0 p-4 border-b">
        <h1 className="text-lg w-full">{t('addOns.manageUsers.title')}</h1>
        <XMarkIcon
          onClick={() => toggleManageUsersModal(false)}
          className="size-6"
        />
      </Modal.Header>
      <Modal.Body className="py-6 px-4 form-control gap-4">
        <div className="flex flex-col gap-3">
          <p>{t('addOns.manageUsers.step1')}</p>
          <div className="flex gap-2 items-center">
            <Button
              size="sm"
              shape="square"
              variant="outline"
              className="rounded"
              disabled={extraSeats <= -subscription.purchasedSeats}
              onClick={() => {
                setExtraSeats((seats) => {
                  const usedSeats = currentSettingsInfo.seats.used;
                  const updatedTotalSeats = seats - 1;

                  if (
                    usedSeats > 0 &&
                    usedSeats >
                      subscription.purchasedSeats +
                        updatedTotalSeats +
                        subscription.includedSeats
                  ) {
                    setUserError(true);

                    return seats;
                  }

                  setUserError(false);

                  return seats - 1;
                });
              }}
              data-testid="seats-decrease-button"
            >
              <MinusIcon className="SubscriptionCheckout__planDetails__seatEditIcon" />
            </Button>
            <Input
              size="sm"
              type="number"
              step="1"
              min={-subscription.purchasedSeats}
              className="SubscriptionCheckout__planDetails__seatsInput"
              onChange={(e) => {
                const usedSeats = currentSettingsInfo.seats.used;
                const parsed = Math.trunc(+e.target.value);
                const shouldShowError =
                  usedSeats > 0 &&
                  usedSeats >
                    subscription.purchasedSeats +
                      parsed +
                      subscription.includedSeats;

                if (e.target.value === '' || Number.isNaN(parsed)) {
                  setUserError(false);
                  setExtraSeats(0);

                  return;
                }

                if (parsed < -subscription.purchasedSeats) {
                  setUserError(false);
                  setExtraSeats(-subscription.purchasedSeats);

                  return;
                }

                if (isLegacyPlan && parsed > subscription.purchasedSeats) {
                  setUserError(false);
                  setExtraSeats(subscription.purchasedSeats);

                  return;
                }

                if (shouldShowError) {
                  setUserError(true);

                  return;
                }

                setUserError(false);
                setExtraSeats(parsed);
              }}
              onBlur={(e) => {
                const usedSeats = currentSettingsInfo.seats.used;
                const updatedTotalSeats = +e.target.value;
                const shouldShowError =
                  usedSeats > 0 &&
                  usedSeats >
                    subscription.purchasedSeats +
                      updatedTotalSeats +
                      subscription.includedSeats;

                if (shouldShowError) {
                  setUserError(true);

                  return;
                }

                setUserError(false);
              }}
              value={extraSeats}
              data-testid="seats-input"
            />
            <Button
              size="sm"
              shape="square"
              variant="outline"
              className="rounded"
              disabled={
                isLegacyPlan && extraSeats >= subscription.purchasedSeats
              }
              onClick={() => {
                setExtraSeats((seats) => seats + 1);
                setUserError(false);
              }}
              data-testid="seats-increase-button"
            >
              <PlusIcon className="SubscriptionCheckout__planDetails__seatEditIcon" />
            </Button>
          </div>
        </div>

        {extraSeats > 0 ? (
          <div className="flex flex-col gap-1">
            <p>{t('addOns.manageUsers.step2')}</p>
            {isEmpty(selectedPaymentMethod.paymentMethodId) ? (
              <Button
                className="SubscriptionCheckout__paymentMethods__addPaymentMethodButton"
                onClick={() => toggleAddPaymentMethodModal(true)}
                color="ghost"
                fullWidth
                data-testid="add-payment-method-button"
              >
                {t('page.SubscriptionCheckout.addPaymentMethodButton')}
              </Button>
            ) : (
              <>
                <CardSelector />
                <Button
                  onClick={() => toggleAddPaymentMethodModal(true)}
                  variant="link"
                  size="sm"
                  className="text-neutral-content w-fit px-0"
                >
                  {t('page.SubscriptionCheckout.addPaymentMethod')}
                </Button>
              </>
            )}
          </div>
        ) : null}

        {userError && (
          <p className="text-error">
            <Trans
              className="text-error"
              i18nKey="settings/SubscriptionManager:addOns.manageUsers.userError"
              components={{
                a: (
                  <a
                    className="text-error underline cursor-pointer"
                    onClick={() => setSelectedTab(SettingsTabs.TEAM)}
                  />
                ),
              }}
            />
          </p>
        )}

        <div className="flex flex-col gap-3">
          {extraSeats > 0 && (
            <p className="text-sm text-neutral-500">
              {t('addOns.manageUsers.proportionalCharge')}
              <br />
              {t('addOns.manageUsers.proportionalChargeDescription', {
                date: conversionFormattedDate,
                cost: costInCents / CENTS_TO_DOLLARS_CONVERSION_FACTOR,
                currency: subscription.currency,
                frequency: t(
                  `subscriptionOverview.frequency.${subscription.frequency}`,
                ),
              })}
            </p>
          )}
          {extraSeats < 0 && (
            <p className="text-sm text-neutral-500">
              {t('addOns.manageUsers.reductionDescription')}
            </p>
          )}
        </div>

        {isMissingPrice && (
          <p className="text-sm text-error" data-testid="manage-users-missing-price-reason">
            {t('addOns.manageUsers.missingPriceReason')}
          </p>
        )}
      </Modal.Body>
      <Modal.Actions className="p-4 bg-base-300 items-center mt-0" data-testid="modal-actions">
        <p className="font-bold text-xl">
          {t('addOns.manageUsers.total', {
            cost: total,
            currency: subscription.currency,
          })}
        </p>
        <p className="text-xs font-normal flex-1 ">
          {t('addOns.manageUsers.monthlyRecurring', {
            frequency: t(
              `subscriptionOverview.frequency.${subscription.frequency}`,
            ),
          })}
        </p>
        <Button
          color="primary"
          size="sm"
          className="rounded"
          disabled={extraSeats === 0 || isMissingPrice || updatingPlan}
          onClick={() => updateSeatsPlan(subscription.purchasedSeats + extraSeats)}
          loading={updatingPlan}
          data-testid="modal-action-button"
        >
          {subscription.purchasedSeats > 0
            ? t('addOns.manageUsers.save')
            : t('addOns.manageUsers.buyNow')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
